import React from 'react';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_GROUP, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { constructQueryParamName } from './SearchPage.shared';
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import SelectMultipleDisplayTypeFilter from './SelectMultipleDisplayTypeFilter/SelectMultipleDisplayTypeFilter';
import SelectGroupMultipleFilter from './SelectGroupMultipleFilter/SelectGroupMultipleFilter';

import { languagesHelper as language } from '../../helpers/languages';

// Helper: get enumOptions in a format that works as query parameter
const createFilterOptions = options =>
  options.map(o => ({ key: `${o.option}`, label: o.label, labelDe: o.labelDe || o.label, icon: o?.icon }));

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    marketplaceCurrency,
    intl,
    showDisplayTypeFilter,
    sortBy,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType, hideOnSearch } = config;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-').toLowerCase();

  if (hideOnSearch) return null;

  let currentLocale = language.get();
  const isDe = currentLocale === "de";

  // Default filters: price, keywords, dates
  switch (schemaType) {
    // case 'price': {
    //   const { min, max, step } = config;
    //   return (
    //     <PriceFilter
    //       id={componentId}
    //       label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
    //       queryParamNames={[key]}
    //       initialValues={initialValues([key], liveEdit)}
    //       onSubmit={getHandleChangedValueFn(useHistoryPush)}
    //       min={min}
    //       max={max}
    //       step={step}
    //       marketplaceCurrency={marketplaceCurrency}
    //       {...rest}
    //     />
    //   );
    // }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    // case 'dates': {
    //   const { dateRangeMode } = config;
    //   const isNightlyMode = dateRangeMode === 'night';
    //   return (
    //     <>
    //       {sortBy}
    //       <BookingDateRangeFilter
    //         id={componentId}
    //         label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
    //         queryParamNames={[key]}
    //         initialValues={initialValues([key], liveEdit)}
    //         onSubmit={getHandleChangedValueFn(useHistoryPush)}
    //         minimumNights={isNightlyMode ? 1 : 0}
    //         {...rest}
    //       />
    //     </>
    //   );
    // }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const queryParamNames = [constructQueryParamName(key, scope)];

      const filterLabel = isDe ? filterConfig.labelDe : filterConfig.label;

      return filterConfig.filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={filterLabel}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSelect={getHandleChangedValueFn(useHistoryPush)}
          options={createFilterOptions(enumOptions)}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={filterLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={createFilterOptions(enumOptions)}
          schemaType={schemaType}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, labelDe,  searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      const filterLabel = isDe ? labelDe : label;
      return (
        <SelectMultipleFilter
          id={componentId}
          label={filterLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={createFilterOptions(enumOptions)}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }

    case SCHEMA_TYPE_GROUP: {
      const { scope, enumOptions, enumOptionsOrder, filterConfig = {} } = config;
      const { label, labelDe, searchMode } = filterConfig;
      const queryParamNames = enumOptionsOrder.map(o => constructQueryParamName(o.filter, scope));
      const filterLabel = isDe ? labelDe : label;

      return (
        <SelectGroupMultipleFilter
          id={componentId}
          label={filterLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          enumOptionsOrder={enumOptionsOrder}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
